import { Controller } from "@hotwired/stimulus"
import { callKarrotBridgeSafely } from "../../../lib/karrot_bridge_utils"
import SnackbarManager from "./utils/snackbar_manager"

// 분쟁조정 확인서 제출을 처리하는 Stimulus 컨트롤러
export default class extends Controller {
  static values = { id: String }
  static targets = ["snackbar"]

  connect() {
    if (this.hasSnackbarTarget) {
      this.snackbarManager = new SnackbarManager(this.snackbarTarget);
    }
  }

  submit(event) {
    if (event) {
      event.preventDefault();
    }

    const form = event?.target?.closest('form') || this.element.querySelector('form');
    if (!form) {
      return;
    }

    // conflict-resolution-form 컨트롤러의 유효성 검사 활용
    const formController = this.application.getControllerForElementAndIdentifier(
      this.element, 'conflict-resolution-form'
    );

    if (formController) {
      const isValid = formController.validator.validateForm();
      if (!isValid) {
        if (formController.hasPageTwoTarget) {
          formController.clearValidationErrors(formController.pageTwoTarget);
          const invalidFields = formController.validator.getInvalidFieldsForPage(2);
          if (invalidFields.length > 0) {
            formController.showValidationErrors(invalidFields);
            invalidFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        const snackbar = formController.snackbarManager || this.snackbarManager;
        if (snackbar) {
          snackbar.show('모든 필수 필드를 입력해주세요.');
        }
        return;
      }
    }

    const submitButton = form.querySelector('input[type="submit"], button[type="submit"]');
    const originalLabel = submitButton?.tagName === 'INPUT' ? submitButton.value : submitButton?.textContent;

    if (submitButton) {
      submitButton.disabled = true;
      if (submitButton.tagName === 'INPUT') {
        submitButton.value = '제출 중...';
      } else {
        submitButton.textContent = '제출 중...';
      }
    }

    const confirmationData = this.collectConfirmationData(form);

    fetch(`/wv/conflict_resolutions/${this.idValue}/submit_confirmation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        conflict_resolution: {
          confirmation_form: confirmationData
        }
      })
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('제출 중 오류가 발생했습니다.');
        }
        return response.json();
      })
      .then(data => {
        if (data.success) {
          this.showCustomAlert(() => {
            if (this.snackbarManager) {
              this.snackbarManager.show('분쟁 사실관계 확인서가 제출되었어요');
            }
            setTimeout(() => {
              callKarrotBridgeSafely(
                (bridge) => bridge.closeRouter({}),
                { fallback: () => window.history.back() }
              );
            }, 2000);
          });
        } else {
          this.showErrorAlert(data.message || '제출 중 오류가 발생했습니다.');
          if (submitButton) {
            submitButton.disabled = false;
            if (submitButton.tagName === 'INPUT') {
              submitButton.value = originalLabel;
            } else {
              submitButton.textContent = originalLabel;
            }
          }
        }
      })
      .catch(error => {
        console.error('Error during submission:', error);
        this.showErrorAlert(error.message || '제출 중 오류가 발생했습니다.');
        if (submitButton) {
          submitButton.disabled = false;
          if (submitButton.tagName === 'INPUT') {
            submitButton.value = originalLabel;
          } else {
            submitButton.textContent = originalLabel;
          }
        }
      });
  }

  collectConfirmationData(form) {
    const allowedFields = new Set([
      'item',
      'completed_at',
      'trade_method',
      'trade_method_detail',
      'payment_amount',
      'payment_method',
      'payment_method_detail',
      'current_item_holder',
      'item_location',
      'started_at',
      'user_selected_conflict_type',
      'conflict_cause_detail',
      'resolution_hope_type',
      'resolution_hope',
      'refund_amount',
      'refund_method',
      'refund_method_detail',
      'counterparty_message'
    ]);

    const confirmationData = {};
    const imageIds = [];
    const formData = new FormData(form);

    formData.forEach((value, key) => {
      if (key === 'conflict_resolution[image_ids][]') {
        if (value) {
          imageIds.push(value);
        }
        return;
      }

      const match = key.match(/^conflict_resolution\[(.+)\]$/);
      if (!match) {
        return;
      }

      const field = match[1];
      if (!allowedFields.has(field)) {
        return;
      }

      // 숫자 필드에서 콤마 제거
      if (field === 'payment_amount' || field === 'refund_amount') {
        confirmationData[field] = value.replace(/,/g, '');
      } else {
        confirmationData[field] = value;
      }
    });

    if (imageIds.length > 0) {
      confirmationData.image_ids = imageIds;
    }

    return confirmationData;
  }

  // 커스텀 알림 창 표시
  showCustomAlert(onConfirm) {
    // 기존 알림이 있으면 제거
    const existingAlert = document.getElementById('custom-alert-container-active');
    if (existingAlert) {
      document.body.removeChild(existingAlert);
    }

    // 템플릿에서 알림창 복제
    const template = document.getElementById('custom-alert-template');
    if (!template) {
      console.error('Custom alert template not found');
      return;
    }

    // 템플릿 내용 복제
    const alertContainer = template.firstElementChild.cloneNode(true);
    alertContainer.id = 'custom-alert-container-active';
    alertContainer.style.display = 'flex';

    // 알림 클릭 이벤트 수정 - 배경 클릭 시에만 닫히도록
    alertContainer.addEventListener('click', (e) => {
      if (e.target === alertContainer) {
        document.body.removeChild(alertContainer);
        if (typeof onConfirm === 'function') {
          onConfirm();
        }
      }
    });

    // 확인 버튼에 별도 클릭 이벤트 추가
    const confirmButton = alertContainer.querySelector('.custom-alert-confirm-button');
    if (confirmButton) {
      confirmButton.addEventListener('click', () => {
        document.body.removeChild(alertContainer);
        if (typeof onConfirm === 'function') {
          onConfirm();
        }
      });
    }

    // 알림 표시
    document.body.appendChild(alertContainer);
  }

  // 에러 알림 표시
  showErrorAlert(message) {
    // 기존 알림이 있으면 제거
    const existingAlert = document.getElementById('error-alert-container-active');
    if (existingAlert) {
      document.body.removeChild(existingAlert);
    }

    // 템플릿에서 알림창 복제
    const template = document.getElementById('error-alert-template');
    if (!template) {
      console.error('Error alert template not found');
      return;
    }

    // 템플릿 내용 복제
    const alertContainer = template.firstElementChild.cloneNode(true);
    alertContainer.id = 'error-alert-container-active';
    alertContainer.style.display = 'flex';

    // 에러 메시지 설정
    const errorMessageElement = alertContainer.querySelector('.error-message');
    if (errorMessageElement) {
      errorMessageElement.textContent = message;
    }

    // 확인 버튼에 이벤트 추가
    const confirmButton = alertContainer.querySelector('.error-alert-confirm-button');
    if (confirmButton) {
      confirmButton.addEventListener('click', (e) => {
        e.stopPropagation();
        document.body.removeChild(alertContainer);
      });
    }

    // 배경 클릭 시 닫히도록 설정
    alertContainer.addEventListener('click', (e) => {
      if (e.target === alertContainer) {
        document.body.removeChild(alertContainer);
      }
    });

    // 알림 표시
    document.body.appendChild(alertContainer);
  }
}
